'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram, MigrationWave } from '@/lib/archimate/extended-types';
import {
  createMigrationPlan,
  createMigrationWave,
  addMigrationWave,
  calculateMigrationProgress,
  generateMigrationTimeline,
  exportMigrationPlan,
} from '@/lib/archimate/migration-utils';
import {
  Truck,
  Plus,
  X,
  Calendar,
  CheckCircle2,
  Circle,
  AlertTriangle,
  Download,
  Clock,
} from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface MigrationPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
  onSelectElement: (elementId: string) => void;
}

export default function MigrationPanel({
  diagram,
  onUpdate,
  onClose,
  onSelectElement,
}: MigrationPanelProps) {
  const [viewMode, setViewMode] = useState<'overview' | 'waves' | 'timeline' | 'create'>(
    diagram.migrationPlan ? 'overview' : 'create'
  );
  const [selectedWave, setSelectedWave] = useState<MigrationWave | null>(null);
  const [newPlanForm, setNewPlanForm] = useState({
    name: '',
    description: '',
    startDate: '',
    targetDate: '',
  });
  const [newWaveForm, setNewWaveForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });
  const [isCreatingWave, setIsCreatingWave] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleCreatePlan = () => {
    if (!newPlanForm.name.trim()) return;

    const plan = createMigrationPlan(
      newPlanForm.name,
      newPlanForm.description || '',
      diagram.id, // current state diagram ID
      diagram.id  // target state diagram ID (same diagram, different future state)
    );

    // Update timeline with user-provided dates if available
    if (newPlanForm.startDate || newPlanForm.targetDate) {
      plan.timeline.start = newPlanForm.startDate || plan.timeline.start;
      plan.timeline.end = newPlanForm.targetDate || plan.timeline.end;
    }

    const updatedDiagram = {
      ...diagram,
      migrationPlan: plan,
    };

    onUpdate(updatedDiagram);
    setNewPlanForm({ name: '', description: '', startDate: '', targetDate: '' });
    setViewMode('overview');
  };

  const handleCreateWave = () => {
    if (
      !diagram.migrationPlan ||
      !newWaveForm.name.trim()
    )
      return;

    // Create the wave with selected elements (empty for now, can be populated later)
    const wave = createMigrationWave(
      newWaveForm.name,
      newWaveForm.description || '',
      [] // elements to migrate in this wave
    );

    // Add the wave to the plan
    const updatedPlan = addMigrationWave(diagram.migrationPlan, wave);

    const updatedDiagram = {
      ...diagram,
      migrationPlan: updatedPlan,
    };

    onUpdate(updatedDiagram);
    setNewWaveForm({ name: '', description: '', startDate: '', endDate: '' });
    setIsCreatingWave(false);
  };

  const handleDeleteWave = (waveId: string) => {
    if (!diagram.migrationPlan) return;

    const updatedDiagram = {
      ...diagram,
      migrationPlan: {
        ...diagram.migrationPlan,
        waves: diagram.migrationPlan.waves.filter((w) => w.id !== waveId),
      },
    };

    onUpdate(updatedDiagram);
    if (selectedWave?.id === waveId) {
      setSelectedWave(null);
    }
  };

  const handleUpdateWaveStatus = (
    waveId: string,
    status: 'planned' | 'in-progress' | 'completed' | 'blocked'
  ) => {
    if (!diagram.migrationPlan) return;

    const updatedDiagram = {
      ...diagram,
      migrationPlan: {
        ...diagram.migrationPlan,
        waves: diagram.migrationPlan.waves.map((wave) =>
          wave.id === waveId ? { ...wave, status } : wave
        ),
      },
    };

    onUpdate(updatedDiagram);
  };

  const handleExportPlan = () => {
    if (!diagram.migrationPlan) return;

    const markdown = exportMigrationPlan(diagram.migrationPlan);
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-migration-plan.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const progress = diagram.migrationPlan
    ? calculateMigrationProgress(diagram.migrationPlan)
    : null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-400" />;
      case 'in-progress':
        return <Clock className="w-4 h-4 text-blue-400 animate-pulse" />;
      case 'blocked':
        return <AlertTriangle className="w-4 h-4 text-red-400" />;
      default:
        return <Circle className="w-4 h-4 text-foreground-muted" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-400 bg-green-500/20 border-green-500/20';
      case 'in-progress':
        return 'text-blue-400 bg-blue-500/20 border-blue-500/20';
      case 'blocked':
        return 'text-red-400 bg-red-500/20 border-red-500/20';
      default:
        return 'text-foreground-tertiary bg-cream/5 border-border';
    }
  };

  const renderCreate = () => {
    return (
      <div className="space-y-4">
        <div className="p-4 bg-cream/5 border border-border rounded-lg space-y-4">
          <h3 className="text-sm font-medium text-foreground">Create Migration Plan</h3>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">Plan Name</label>
            <input
              type="text"
              value={newPlanForm.name}
              onChange={(e) => setNewPlanForm({ ...newPlanForm, name: e.target.value })}
              placeholder="e.g., Cloud Migration 2025"
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-foreground-secondary">Description</label>
            <textarea
              value={newPlanForm.description}
              onChange={(e) => setNewPlanForm({ ...newPlanForm, description: e.target.value })}
              placeholder="Describe the migration plan..."
              rows={3}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-sm text-foreground-secondary">Start Date</label>
              <input
                type="date"
                value={newPlanForm.startDate}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, startDate: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-foreground-secondary">Target Date</label>
              <input
                type="date"
                value={newPlanForm.targetDate}
                onChange={(e) => setNewPlanForm({ ...newPlanForm, targetDate: e.target.value })}
                className="w-full px-3 py-2 bg-background border border-border text-foreground rounded focus:outline-none focus:border-accent"
              />
            </div>
          </div>

          <button
            onClick={handleCreatePlan}
            disabled={
              !newPlanForm.name.trim() || !newPlanForm.startDate || !newPlanForm.targetDate
            }
            className="w-full px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Create Migration Plan
          </button>
        </div>

        <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-foreground-secondary">
          <div className="font-medium text-blue-400 mb-2">About Migration Plans</div>
          <div className="text-xs text-foreground-tertiary space-y-1">
            <div>• Break down migrations into phases (waves)</div>
            <div>• Track progress and dependencies</div>
            <div>• Identify risks and blockers</div>
            <div>• Generate timelines and reports</div>
          </div>
        </div>
      </div>
    );
  };

  const renderOverview = () => {
    if (!diagram.migrationPlan) return null;

    return (
      <div className="space-y-4">
        {/* Plan Header */}
        <div className="p-4 bg-cream/5 rounded-lg">
          <h3 className="text-lg font-medium text-foreground mb-2">{diagram.migrationPlan.name}</h3>
          {diagram.migrationPlan.description && (
            <div className="text-sm text-foreground-secondary mb-3">{diagram.migrationPlan.description}</div>
          )}
          <div className="flex items-center gap-4 text-xs text-foreground-tertiary">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(diagram.migrationPlan.timeline.start).toLocaleDateString()}
            </div>
            <span>→</span>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(diagram.migrationPlan.timeline.end).toLocaleDateString()}
            </div>
          </div>
        </div>

        {/* Progress */}
        {progress && (
          <div className="p-4 bg-cream/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-foreground-secondary">Overall Progress</span>
              <span className="text-sm font-medium text-accent">
                {progress.completed} / {progress.total} waves
              </span>
            </div>
            <div className="w-full h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-500"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <div className="text-xs text-foreground-tertiary mt-2 text-right">
              {progress.percentage.toFixed(1)}% complete
            </div>
          </div>
        )}

        {/* Waves Summary */}
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-cream/5 rounded-lg">
            <div className="text-xs text-foreground-tertiary mb-1">Total Waves</div>
            <div className="text-2xl font-bold text-foreground">{diagram.migrationPlan.waves.length}</div>
          </div>
          <div className="p-3 bg-cream/5 rounded-lg">
            <div className="text-xs text-foreground-tertiary mb-1">Total Elements</div>
            <div className="text-2xl font-bold text-foreground">
              {diagram.migrationPlan.waves.reduce((sum, w) => sum + w.elements.length, 0)}
            </div>
          </div>
        </div>

        {/* Status Breakdown */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-foreground">By Status</h4>
          <div className="grid grid-cols-2 gap-2">
            {(['planned', 'in-progress', 'completed', 'blocked'] as const).map((status) => {
              const count = diagram.migrationPlan!.waves.filter((w) => w.status === status).length;
              return (
                <div key={status} className={`p-3 rounded-lg border ${getStatusColor(status)}`}>
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusIcon(status)}
                    <span className="text-xs capitalize">{status.replace(/-/g, ' ')}</span>
                  </div>
                  <div className="text-lg font-medium">{count}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('waves')}
            className="flex-1 px-4 py-2 bg-accent-muted hover:bg-accent-muted border border-border text-accent rounded transition-colors"
          >
            Manage Waves
          </button>
          <button
            onClick={handleExportPlan}
            className="px-4 py-2 bg-cream/10 hover:bg-cream/20 text-foreground rounded transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>
    );
  };

  const renderWaves = () => {
    if (!diagram.migrationPlan) return null;

    return (
      <div className="space-y-4">
        {/* Create Wave Form */}
        {isCreatingWave ? (
          <div className="p-4 bg-cream/5 border border-border rounded-lg space-y-3">
            <h4 className="text-sm font-medium text-foreground">Create New Wave</h4>
            <input
              type="text"
              value={newWaveForm.name}
              onChange={(e) => setNewWaveForm({ ...newWaveForm, name: e.target.value })}
              placeholder="Wave name (e.g., Wave 1: Core Services)"
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent"
            />
            <textarea
              value={newWaveForm.description}
              onChange={(e) => setNewWaveForm({ ...newWaveForm, description: e.target.value })}
              placeholder="Description (optional)"
              rows={2}
              className="w-full px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent resize-none"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={newWaveForm.startDate}
                onChange={(e) => setNewWaveForm({ ...newWaveForm, startDate: e.target.value })}
                className="px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent"
              />
              <input
                type="date"
                value={newWaveForm.endDate}
                onChange={(e) => setNewWaveForm({ ...newWaveForm, endDate: e.target.value })}
                className="px-3 py-2 bg-background border border-border text-foreground rounded text-sm focus:outline-none focus:border-accent"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCreateWave}
                disabled={
                  !newWaveForm.name.trim() || !newWaveForm.startDate || !newWaveForm.endDate
                }
                className="flex-1 px-4 py-2 bg-accent hover:bg-accent-hover text-accent-foreground font-medium rounded text-sm transition-colors disabled:opacity-50"
              >
                Create Wave
              </button>
              <button
                onClick={() => {
                  setIsCreatingWave(false);
                  setNewWaveForm({ name: '', description: '', startDate: '', endDate: '' });
                }}
                className="px-4 py-2 bg-cream/10 hover:bg-cream/20 text-foreground rounded text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsCreatingWave(true)}
            className="w-full px-4 py-3 bg-accent-muted hover:bg-accent-muted border border-border text-accent rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create New Wave
          </button>
        )}

        {/* Wave List */}
        {diagram.migrationPlan.waves.length === 0 ? (
          <div className="p-8 text-center text-foreground-tertiary text-sm">
            No migration waves defined yet. Create a wave to get started.
          </div>
        ) : (
          <div className="space-y-3">
            {diagram.migrationPlan.waves
              .sort((a, b) => {
                const aDate = a.startDate ? new Date(a.startDate).getTime() : 0;
                const bDate = b.startDate ? new Date(b.startDate).getTime() : 0;
                return aDate - bDate;
              })
              .map((wave, index) => {
                const isSelected = selectedWave?.id === wave.id;

                return (
                  <div
                    key={wave.id}
                    className={`p-4 rounded-lg border transition-all ${
                      isSelected
                        ? 'bg-accent-muted border-accent'
                        : 'bg-cream/5 border-border hover:bg-cream/10'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-foreground">
                            {wave.name || `Wave ${index + 1}`}
                          </span>
                          {getStatusIcon(wave.status)}
                        </div>
                        {wave.description && (
                          <div className="text-sm text-foreground-secondary mb-2">{wave.description}</div>
                        )}
                        <div className="flex items-center gap-3 text-xs text-foreground-tertiary">
                          <span>{wave.elements.length} elements</span>
                          {wave.startDate && wave.endDate && (
                            <>
                              <span>•</span>
                              <span>
                                {new Date(wave.startDate).toLocaleDateString()} →{' '}
                                {new Date(wave.endDate).toLocaleDateString()}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteWave(wave.id)}
                        className="p-2 hover:bg-red-500/20 text-foreground-tertiary hover:text-red-400 rounded transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Status Controls */}
                    <div className="flex gap-2">
                      {(['planned', 'in-progress', 'completed', 'blocked'] as const).map(
                        (status) => (
                          <button
                            key={status}
                            onClick={() => handleUpdateWaveStatus(wave.id, status)}
                            className={`flex-1 px-2 py-1.5 text-xs rounded capitalize transition-colors ${
                              wave.status === status
                                ? getStatusColor(status)
                                : 'bg-cream/5 text-foreground-tertiary hover:bg-cream/10'
                            }`}
                          >
                            {status.replace(/-/g, ' ')}
                          </button>
                        )
                      )}
                    </div>

                    {/* Elements List (when selected) */}
                    {isSelected && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-medium text-foreground-secondary">Elements</div>
                          <div className="text-xs text-foreground-tertiary">{wave.elements.length} selected</div>
                        </div>

                        {/* Element Selection */}
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                          {diagram.elements.map((element) => {
                            const isInWave = wave.elements.includes(element.id);
                            return (
                              <label
                                key={element.id}
                                className="flex items-center gap-2 p-2 bg-cream/5 hover:bg-cream/10 rounded transition-colors cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={isInWave}
                                  onChange={(e) => {
                                    const updatedWave = {
                                      ...wave,
                                      elements: e.target.checked
                                        ? [...wave.elements, element.id]
                                        : wave.elements.filter((id) => id !== element.id),
                                    };

                                    const updatedDiagram = {
                                      ...diagram,
                                      migrationPlan: {
                                        ...diagram.migrationPlan!,
                                        waves: diagram.migrationPlan!.waves.map((w) =>
                                          w.id === wave.id ? updatedWave : w
                                        ),
                                      },
                                    };

                                    onUpdate(updatedDiagram);
                                  }}
                                  className="w-4 h-4 accent-orange"
                                />
                                <button
                                  type="button"
                                  className="flex-1 flex items-center justify-between bg-transparent border-none p-0 cursor-pointer text-left"
                                  onClick={() => onSelectElement(element.id)}
                                >
                                  <span className="text-sm text-foreground-secondary">{element.name}</span>
                                  <span className="text-xs text-foreground-tertiary capitalize">
                                    {element.type.replace(/-/g, ' ')}
                                  </span>
                                </button>
                              </label>
                            );
                          })}

                          {diagram.elements.length === 0 && (
                            <div className="text-xs text-foreground-tertiary text-center py-4">
                              No elements in diagram
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Expand/Collapse */}
                    <button
                      onClick={() => setSelectedWave(isSelected ? null : wave)}
                      className="mt-3 w-full text-xs text-accent hover:text-accent transition-colors"
                    >
                      {isSelected ? 'Hide' : 'Manage'} elements ({wave.elements.length})
                    </button>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    );
  };

  const renderTimeline = () => {
    if (!diagram.migrationPlan) return null;

    const waves = diagram.migrationPlan.waves;

    return (
      <div className="space-y-4">
        {waves.length === 0 ? (
          <div className="p-8 text-center text-foreground-tertiary text-sm">
            No timeline available. Add waves to see the migration timeline.
          </div>
        ) : (
          <div className="space-y-6">
            {waves.map((wave) => (
              <div key={wave.id} className="relative pl-8">
                {/* Timeline dot */}
                <div
                  className={`absolute left-0 top-1 w-3 h-3 rounded-full border-2 ${
                    wave.status === 'completed'
                      ? 'bg-green-500 border-green-500'
                      : wave.status === 'in-progress'
                      ? 'bg-blue-500 border-blue-500'
                      : wave.status === 'blocked'
                      ? 'bg-red-500 border-red-500'
                      : 'bg-cream/20 border-border/40'
                  }`}
                />

                {/* Timeline line */}
                <div className="absolute left-[5px] top-5 bottom-0 w-0.5 bg-cream/20" />

                {/* Content */}
                <div className="pb-6">
                  <div className="p-4 bg-cream/5 rounded-lg">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-foreground mb-1">{wave.name}</div>
                        {wave.startDate && wave.endDate && (
                          <div className="text-xs text-foreground-tertiary">
                            {wave.startDate} → {wave.endDate}
                          </div>
                        )}
                      </div>
                      {getStatusIcon(wave.status)}
                    </div>
                    {wave.description && (
                      <div className="text-sm text-foreground-secondary mt-2">{wave.description}</div>
                    )}
                    <div className="text-xs text-foreground-tertiary mt-2">
                      {wave.elements.length} element
                      {wave.elements.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed top-0 right-0 h-full w-[600px] bg-background/95 backdrop-blur border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Truck className="w-6 h-6 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Migration Planning</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button
          onClick={onClose}
          className="p-2 hover:bg-cream/10 rounded-lg transition-colors"
        >
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="migration"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      {diagram.migrationPlan && (
        <div className="flex items-center gap-2 p-4 border-b border-border">
          <button
            onClick={() => setViewMode('overview')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'overview'
                ? 'bg-accent text-accent-foreground'
                : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setViewMode('waves')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'waves'
                ? 'bg-accent text-accent-foreground'
                : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Waves
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'timeline'
                ? 'bg-accent text-accent-foreground'
                : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Timeline
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {!diagram.migrationPlan && viewMode === 'create' && renderCreate()}
        {diagram.migrationPlan && viewMode === 'overview' && renderOverview()}
        {diagram.migrationPlan && viewMode === 'waves' && renderWaves()}
        {diagram.migrationPlan && viewMode === 'timeline' && renderTimeline()}
      </div>
    </div>
  );
}
